import { Skeleton } from "@mui/material";
import { MailEmailLetter } from "@pmag/icons";
import { Button, MenuItem, Typography } from "@pmag/ui-components";
import { type Maybe } from "@src/graphql.generated";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { AddToCartButton } from "@src/pages/subGroupDetail/subgroupDetailsListSection/addToCartButton/AddToCartButton";
import { dataTestId } from "@src/pages/subGroupDetail/subgroupDetailsListSection/sparePartsList/SparePartsList";
import { WishlistIconButton } from "@src/pages/subGroupDetail/subgroupDetailsListSection/wishlistIconButton/WishlistIconButton";
import { dispatchSendRequestEvent, SPFAvailabilityStatus } from "@src/utils/events";
import * as React from "react";
import { FormattedMessage, FormattedNumber } from "react-intl";

import { PriceCellContainer, PriceCellRightContainer, StyledSelect } from "./PriceCell.styles";

export interface PriceCellItem {
    article: Maybe<{
        articleNumber: string;
        assetPath: Maybe<string>;
        id: string;
        name: Maybe<string>;
    }>;
    clientArticleInfo?: {
        availability: SPFAvailabilityStatus;
        clientArticleNumber?: string;
        currency?: string;
        price?: number;
    };
    id: string;
    quantity: number;
}

interface PriceCellProps {
    brand: string;
    clientWishlist: string[];
    item: PriceCellItem;
    modelId: string;
    showCellInWishlist?: boolean;
}

export const PriceCell: React.FunctionComponent<PriceCellProps> = ({ brand, modelId, item, clientWishlist, showCellInWishlist = false }) => {
    const { hasArticleInfos, hasAddToCart, priceInfoDisclaimer, sendRequestEnabled } = useAttributeConfig();
    const [amount, setAmount] = React.useState(item.quantity > 0 ? item.quantity : 1);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        setTimeout(() => {
            setLoading(false);
        }, 5000);
    }, []);

    const showAddToCartButton =
        hasArticleInfos &&
        hasAddToCart &&
        (item.clientArticleInfo?.availability === SPFAvailabilityStatus.Available ||
            item.clientArticleInfo?.availability === SPFAvailabilityStatus.ManufacturerAvailable);

    const showSendRequestButton =
        sendRequestEnabled &&
        (!hasArticleInfos ||
            item.clientArticleInfo?.availability === SPFAvailabilityStatus.NotAvailable ||
            item.clientArticleInfo?.availability === SPFAvailabilityStatus.Unknown);

    return (
        <PriceCellContainer>
            {hasArticleInfos &&
                (loading && !item.clientArticleInfo ? (
                    <Skeleton height={20} width={50} />
                ) : (
                    <Typography variant="heading-09" data-testid={dataTestId.cellRetailPrice}>
                        {item.clientArticleInfo && item.clientArticleInfo.price ? (
                            <>
                                <FormattedNumber value={item.clientArticleInfo.price} currency={item.clientArticleInfo.currency} style="currency" />

                                {priceInfoDisclaimer && <sup>*</sup>}
                            </>
                        ) : (
                            <FormattedMessage id="sparePartsFinder.subgroupPage.sparePartsList.placeholder" defaultMessage="-" />
                        )}
                    </Typography>
                ))}

            <PriceCellRightContainer>
                {showAddToCartButton && item.article && (
                    <>
                        <StyledSelect
                            onChange={(value) => setAmount(value.target.value as number)}
                            value={amount}
                            variant="outlined"
                            size="small"
                            // @ts-ignore next line
                            tabIndex={0}
                        >
                            {Array(20)
                                .fill(null)
                                .map((_, index) => (
                                    <MenuItem key={`amount-select-${index}`} value={index + 1}>{`${index + 1}`}</MenuItem>
                                ))}
                        </StyledSelect>

                        <AddToCartButton
                            articleNumber={item.article.articleNumber}
                            brand={brand}
                            amount={amount}
                            clientArticleNumber={item.clientArticleInfo?.clientArticleNumber}
                        />
                    </>
                )}

                {showSendRequestButton && item.article && (
                    <Button
                        variant="secondary"
                        size="small"
                        icon={<MailEmailLetter size="small" />}
                        onClick={() => item.article && dispatchSendRequestEvent(item.article.articleNumber, brand)}
                    >
                        <FormattedMessage id="sparePartsFinder.subgroupPage.sparePartsList.sendRequest" defaultMessage="Send request" />
                    </Button>
                )}

                <WishlistIconButton
                    brand={brand}
                    modelId={modelId}
                    item={item}
                    showCellInWishlist={showCellInWishlist}
                    clientWishlist={clientWishlist}
                />
            </PriceCellRightContainer>
        </PriceCellContainer>
    );
};
